import { Icon } from '@mui/material'
import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import { tripDateUpdateError } from '@returnmates/client-core/src/constants/errors'
import { SourceType } from '@returnmates/client-core/src/constants/sourceType'
import {
  PackageStatus,
  PreviousAddressSource,
  Trip,
  TripStatus,
} from '@returnmates/client-core/src/graphql/generated/api'
import { getCurrentTrip } from '@returnmates/client-core/src/selectors/trip'
import { getCurrentUser } from '@returnmates/client-core/src/selectors/user'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import areArraysOfStringEqual from '@returnmates/client-core/src/utils/areArraysOfStringEqual'
import { checkIsAddressChanged } from '@returnmates/client-core/src/utils/checkIsAddressChanged'
import { decodeCsatText, encodeCsatText } from '@returnmates/client-core/src/utils/csatText'
import errorMapper from '@returnmates/client-core/src/utils/errorMapper'
import {
  getDeliveredStatusError,
  getRTSStatusError,
} from '@returnmates/client-core/src/utils/errorMultipleTripUpdate'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import tripValidation from '@returnmates/client-core/src/utils/validation/pickupDetails'
import Button from '@returnmates/ui-core/src/components/Button'
import ErrorBlock from '@returnmates/ui-core/src/components/ErrorBlock'
import SwaySpinner from '@returnmates/ui-core/src/components/SwaySpinner'
import clsx from 'clsx'
import { FORM_ERROR } from 'final-form'
import arrayMutators from 'final-form-arrays'
import moment from 'moment'
import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'

import { FlyoutSidebarContext } from '../../pages/Main'
import RescheduleWhiteIcon from '../images/icons/rescheduleWhiteIcon'
import EditPickupFormInner from './components/EditPickupFormInner'
import RescheduleModal from './components/RescheduleModal'
import useStyles from './styles'

const DEFAULT_SCHEDULER_LIMIT = 7

interface Props {
  id: string
}

function EditPickupForm({ id }: Props) {
  const classes = useStyles()
  const currentUser = useSelector(getCurrentUser)
  const [isTripUpdateLoading, setIsTripUpdateLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isCalendarLoading, setIsCalendarLoading] = useState(false)
  const currentTrip: Trip | null = useSelector(getCurrentTrip)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const { handleCloseSidebar } = useContext(FlyoutSidebarContext)

  const dispatch = useDispatch()

  const updateAddress = useCallback(
    async address => {
      const currentAddress = currentUser?.addresses.find(
        userAddress => userAddress?.id === currentTrip?.address.id,
      )

      if (checkIsAddressChanged({ updatedAddress: address, currentAddress })) {
        try {
          await createAsyncAction(
            dispatch,
            actions.updateAddress.request({
              id: address.id,
              input: {
                name: address.name,
                phone: address.phone,
                street: address.street,
                city: address.city,
                state: address.state,
                zipCode: address.zipCode,
                unit: address.unit,
                instructions: address.instructions,
                isValid: address.isValid,
              },
            }),
          )

          if (currentAddress) {
            await createAsyncAction(
              dispatch,
              actions.upsertPreviousAddressTrip.request({
                tripId: id,
                input: {
                  addressId: currentAddress.id,
                  name: currentAddress.name,
                  phone: currentAddress.phone,
                  street: currentAddress.street,
                  city: currentAddress.city,
                  state: currentAddress.state,
                  zipCode: currentAddress.zipCode,
                  unit: currentAddress.unit,
                  instructions: currentAddress.instructions,
                  source: PreviousAddressSource.WILSON,
                  userId: currentUser?.id,
                },
              }),
            )
          }
        } catch (err) {
          const { message } = err as Error

          dispatch(
            actions.addSnackBar.request({
              type: SnackBarStatuses.ERROR,
              message: errorMapper(message || (err as string)),
            }),
          )
        }
      }
    },
    [currentTrip?.address.id, currentUser?.addresses, dispatch, id],
  )

  const updateTripCSAT = useCallback(
    async ({ tripCSATScore, tripCSATReason, tripCSATText }) => {
      const formattedTripCSATScore = Number(tripCSATScore) === 0 ? null : Number(tripCSATScore)
      const isCSATReasonsEqual = areArraysOfStringEqual(
        tripCSATReason ?? [],
        currentTrip?.tripCSATReason ?? [],
      )

      // The form holds the decoded text, so compare against the decoded stored value
      const tripCSATUpdated =
        formattedTripCSATScore !== currentTrip?.tripCSATScore ||
        (tripCSATText ?? '') !== decodeCsatText(currentTrip?.tripCSATText) ||
        !isCSATReasonsEqual

      if (tripCSATUpdated) {
        await createAsyncAction(
          dispatch,
          actions.updateTripCSAT.request({
            input: {
              tripId: id,
              tripCSATScore: formattedTripCSATScore ?? null,
              tripCSATText: encodeCsatText(tripCSATText),
              tripCSATReason: tripCSATReason,
            },
          }),
        )
      }
    },
    [
      currentTrip?.tripCSATReason,
      currentTrip?.tripCSATScore,
      currentTrip?.tripCSATText,
      dispatch,
      id,
    ],
  )

  const updateTrip = useCallback(
    async (formTrip: Trip) => {
      const {
        method,
        status,
        date,
        notes,
        completionNotes,
        tripCxlReason,
        failureReason,
        successReason,
        isTripWithWrongPickedUpItem,
        address,
      } = formTrip

      await createAsyncAction(
        dispatch,
        actions.updateTrip.request({
          id,
          input: {
            date: moment(date).format('YYYY-MM-DD'),
            method,
            status,
            notes,
            completionNotes,
            updateSource: SourceType.WILSON,
            tripCxlReason,
            failureReason,
            successReason,
            isTripWithWrongPickedUpItem,
            addressId: address.id,
          },
        }),
      )
    },
    [dispatch, id],
  )

  const onSubmit = useCallback(
    async (formTrip: Trip) => {
      const packagesIdsWithRTSStatus = currentTrip?.packages
        .filter(p => p?.status === PackageStatus.RTS)
        .map(pack => pack?.id)
      const packagesIdsWithDeliveredStatus = currentTrip?.packages
        .filter(p => p?.status === PackageStatus.DELIVERED)
        .map(pack => pack?.id)

      if (packagesIdsWithDeliveredStatus?.length) {
        packagesIdsWithDeliveredStatus.forEach(packId => {
          dispatch(
            actions.addSnackBar.request({
              type: SnackBarStatuses.ERROR,
              message: getDeliveredStatusError(packId as string),
            }),
          )
        })

        return
      }

      // TODO: Move this logic to BE
      if (formTrip.date !== currentTrip?.date && packagesIdsWithDeliveredStatus?.length) {
        packagesIdsWithDeliveredStatus.forEach(packId => {
          dispatch(
            actions.addSnackBar.request({
              type: SnackBarStatuses.ERROR,
              message: getDeliveredStatusError(packId as string),
            }),
          )
        })

        return
      }

      // TODO: Review this and if it's still valid then move this logic to BE
      if (packagesIdsWithRTSStatus?.length) {
        packagesIdsWithRTSStatus.forEach(packId => {
          dispatch(
            actions.addSnackBar.request({
              type: SnackBarStatuses.ERROR,
              message: getRTSStatusError(packId as string),
            }),
          )
        })

        return
      }

      if (
        (currentTrip?.status === TripStatus.PENDING ||
          currentTrip?.status === TripStatus.PENDING_API) &&
        !moment(formTrip.date).isSame(currentTrip?.date)
      ) {
        dispatch(
          actions.addSnackBar.request({
            type: SnackBarStatuses.ERROR,
            message: tripDateUpdateError(currentTrip?.status),
          }),
        )

        return
      }

      setIsTripUpdateLoading(true)

      try {
        await updateAddress(formTrip.address)

        await updateTripCSAT(formTrip)

        await updateTrip(formTrip)
      } catch (err) {
        const { message } = err as Error

        return { [FORM_ERROR]: errorMapper(message || (err as string)) }
      } finally {
        setIsTripUpdateLoading(false)
      }
    },
    [
      currentTrip?.packages,
      currentTrip?.date,
      currentTrip?.status,
      dispatch,
      updateAddress,
      updateTripCSAT,
      updateTrip,
    ],
  )

  const getTrip = useCallback(async () => {
    try {
      if (id && id !== currentTrip?.id) {
        setIsLoading(true)

        await createAsyncAction(
          dispatch,
          actions.getTrip.request({
            id,
          }),
        )
      }
    } catch (error) {
      dispatch(
        actions.addSnackBar.request({
          type: SnackBarStatuses.ERROR,
          message: error,
        }),
      )
    } finally {
      setIsLoading(false)
    }
  }, [id, currentTrip?.id, dispatch])

  const handleOpenCalendar = () => {
    setIsCalendarOpen(true)
  }

  const handleCloseCalendar = () => {
    setIsCalendarOpen(false)
  }

  const schedulerLimit = useMemo(() => {
    if (!currentTrip?.packages?.length) {
      return DEFAULT_SCHEDULER_LIMIT
    }
    const partner = currentTrip.packages[0]?.partner
    return partner?.schedulerLimit ?? DEFAULT_SCHEDULER_LIMIT
  }, [currentTrip?.packages])

  const maxDate = useMemo(() => {
    if (!schedulerLimit) return null
    return moment()
      .add(schedulerLimit, 'days')
      .format('YYYY-MM-DD')
  }, [schedulerLimit])

  const handleRescheduleTrip = useCallback(
    async (date: string) => {
      setIsCalendarLoading(true)

      try {
        await createAsyncAction(
          dispatch,
          actions.reschedule.request({
            tripIds: [id],
            date: moment(date).format('YYYY-MM-DD'),
            updateSource: SourceType.WILSON,
          }),
        )

        handleCloseCalendar()
      } catch (err) {
        const { message } = err as Error
        dispatch(
          actions.addSnackBar.request({
            type: SnackBarStatuses.ERROR,
            message: errorMapper(message || (err as string)),
          }),
        )
      } finally {
        setIsCalendarLoading(false)
      }
    },
    [dispatch, id],
  )

  // The form works with the decoded CSAT text; keep a stable reference per trip
  const initialValues = useMemo(
    () =>
      currentTrip ? { ...currentTrip, tripCSATText: decodeCsatText(currentTrip.tripCSATText) } : {},
    [currentTrip],
  )

  useEffect(() => {
    getTrip()
  }, [getTrip])

  if (isLoading) {
    return <SwaySpinner classes={{ spinnerWrapper: classes.spinner }} />
  }

  return (
    <Form
      initialValues={initialValues}
      validate={tripValidation}
      onSubmit={onSubmit}
      mutators={{ ...arrayMutators }}
      render={({ handleSubmit, submitError }) => (
        <>
          <ErrorBlock message={submitError} className={classes.errorBlock} />
          {currentTrip ? <EditPickupFormInner currentTrip={currentTrip} /> : null}
          <RescheduleModal
            isLoading={isCalendarLoading}
            isOpen={isCalendarOpen}
            handleClose={handleCloseCalendar}
            handleSubmit={handleRescheduleTrip}
            maxDate={maxDate}
          />
          <div className={classes.buttonsContainer}>
            <Button
              onClick={handleOpenCalendar}
              label="Reschedule"
              disabled={currentTrip?.hasRescheduled || false}
              disabledStyles={classes.rescheduleDisabledButton}
              startIcon={<Icon component={RescheduleWhiteIcon} />}
              className={clsx(classes.button, classes.rescheduleButton)}
            />
            <Button
              onClick={handleCloseSidebar}
              label="Cancel"
              className={clsx(classes.button, classes.cancelButton)}
              cancelButton
            />
            <Button
              onClick={handleSubmit}
              label="Save"
              isLoading={isTripUpdateLoading}
              className={clsx(classes.button, classes.saveButton)}
            />
          </div>
        </>
      )}
    />
  )
}

export default memo(EditPickupForm)
